import { Modal, Text, TextArea } from '@viapeople/components'
import type React from 'react'
import { useEffect, useState } from 'react'
import { copyToClipboard, downloadJSONFile, exportFormToJSON, shouldDownloadJSON } from '../helpers/formExportImport'
import type { Form } from '../types'

interface ExportFormDialogProps {
	isOpen: boolean
	onClose: () => void
	form: Form
}

export const ExportFormDialog: React.FC<ExportFormDialogProps> = ({ isOpen, onClose, form }) => {
	const [jsonString, setJsonString] = useState('')
	const [copied, setCopied] = useState(false)
	const [shouldDownload, setShouldDownload] = useState(false)

	useEffect(() => {
		if (isOpen && form) {
			const json = exportFormToJSON(form)
			setJsonString(json)
			setShouldDownload(shouldDownloadJSON(json))
			setCopied(false)
		}
	}, [isOpen, form])

	const handleCopy = async () => {
		const success = await copyToClipboard(jsonString)
		if (success) {
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		}
	}

	const handleDownload = () => {
		const fileName = `${form.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_export.json`
		downloadJSONFile(jsonString, fileName)
		onClose()
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={`Export Form: ${form.name}`}
			buttons={
				shouldDownload
					? [
							{
								children: 'Cancel',
								variant: 'secondary',
								onClick: onClose,
							},
							{
								children: 'Download JSON',
								variant: 'primary',
								onClick: handleDownload,
							},
						]
					: [
							{
								children: 'Close',
								variant: 'secondary',
								onClick: onClose,
							},
							{
								children: copied ? 'Copied!' : 'Copy to Clipboard',
								variant: 'primary',
								onClick: handleCopy,
							},
						]
			}>
			{shouldDownload ? (
				<Text variant='small'>
					This form is large ({Math.round(jsonString.length / 1024)}KB). Click the button below to download it as a JSON
					file.
				</Text>
			) : (
				<>
					<Text variant='small' className='mb-4'>
						Copy the JSON below to export this form:
					</Text>
					<TextArea
						value={jsonString}
						readOnly
						className='font-mono text-sm'
						rows={20}
						onClick={(e) => (e.target as HTMLTextAreaElement).select()}
					/>
				</>
			)}
		</Modal>
	)
}
